import { CustomError } from './CustomError'
import { PuError } from './PuError'

/**
 * Base error class for API response validation failures
 * Provides consistent error messages for Sentry grouping while preserving context data for debugging
 *
 * This class standardizes validation error handling across all API repositories.
 * Usage: new BaseApiValidationError('calendar-availability', url, params, response, originalError)
 */
export class BaseApiValidationError extends CustomError implements PuError {
  readonly statusCode: number = ERROR_CODE_API_VALIDATION

  public readonly endpoint: string
  public readonly url: string
  public readonly params: Record<string, any>
  public readonly rawResponse: any
  public readonly validationErrors: any[]
  public readonly originalError: any

  constructor(
    endpoint: string,
    url: string,
    params: Record<string, any>,
    rawResponse: any,
    originalError: any,
  ) {
    // Generate consistent message for Sentry grouping (no variable data)
    const message = `${endpoint} response validation failed`

    super({
      name: 'BaseApiValidationError',
      message,
      cause: originalError,
    })

    // Store context data (variable data that shouldn't be in the error message)
    this.endpoint = endpoint
    this.url = url
    this.params = params
    this.rawResponse = rawResponse
    this.validationErrors = originalError?.issues || []

    // Store originalError as non-enumerable to prevent SSR serialization issues
    // when this error is passed to Nuxt's error page
    Object.defineProperty(this, 'originalError', {
      value: originalError,
      enumerable: false,
      writable: false,
      configurable: true,
    })
  }

  /**
   * Get context data for Sentry or logging
   * This includes all the variable data that shouldn't be in the error message
   */
  getContext() {
    return {
      endpoint: this.endpoint,
      url: this.url,
      params: JSON.stringify(this.params),
      rawResponse: JSON.stringify(this.rawResponse),
      validationErrors: JSON.stringify(this.validationErrors),
      originalErrorMessage: this.originalError?.message,
      stack: this.stack || 'No stack trace available',
    }
  }
}

export const ERROR_CODE_API_VALIDATION = 422
